<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderCustomFields.vue.
  // Values are written immutably (new object per keystroke — Vue 2 reactivity).
  import { computed } from 'vue';
  import { usePosDraftsStore } from '@/stores/pos-drafts.store';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';

  const posDrafts = usePosDraftsStore();

  const draft = computed(() => posDrafts.activeDraft);

  const activeFields = computed(() => {
    const type = draft.value?.type;
    if (!type) return [];
    return posDrafts.getActiveCustomFields(type);
  });

  function getFieldValue(fieldId: number): string {
    return draft.value?.customFieldValues[String(fieldId)] ?? '';
  }

  function updateFieldValue(fieldId: number, value: string) {
    if (!draft.value) return;
    posDrafts.updateDraft(draft.value.id, {
      customFieldValues: {
        ...draft.value.customFieldValues,
        [String(fieldId)]: value,
      },
    });
  }
</script>

<template>
  <div v-if="activeFields.length > 0" class="pos-v2-custom-fields">
    <div class="fields-header">
      <PosUiIcon name="format-list-checks" class="header-icon" />
      <span class="header-text">{{ $t('posV2.additionalData') }}</span>
    </div>

    <div class="fields-list">
      <div v-for="field in activeFields" :key="field.id" class="field-row">
        <label class="field-label">
          {{ field.title }}
          <span v-if="field.mandatory" class="required-mark">*</span>
        </label>
        <PosUiInput
          :value="getFieldValue(field.id)"
          :placeholder="
            $t('posV2.customFieldPlaceholder', {
              field: field.title.toLowerCase(),
            })
          "
          size="md"
          :maxlength="255"
          class="field-input"
          @input="(value) => updateFieldValue(field.id, value)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-custom-fields {
    @apply tw-flex tw-flex-col tw-gap-2;

    .fields-header {
      @apply tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-neutral-700;

      .header-icon {
        @apply tw-flex-shrink-0 tw-text-base tw-text-orange-600;
      }

      .header-text {
        @apply tw-truncate;
      }
    }

    .fields-list {
      @apply tw-flex tw-flex-col tw-gap-2;
    }

    .field-row {
      @apply tw-flex tw-flex-col tw-gap-1;
    }

    .field-label {
      @apply tw-text-xs tw-font-medium tw-text-neutral-700;

      .required-mark {
        @apply tw-ml-0.5 tw-text-red-600;
      }
    }

    .field-input {
      @apply tw-w-full;
    }
  }
</style>
